import { useTranslation } from 'react-i18next';

import { IMAGE_CONSTANTS } from '@/constants';

import type { TaskDetail } from '@/types';

interface CompletedInsightsBarProps {
  tasks: TaskDetail[];
}

/**
 * Completed tab insights bar — matches Xamarin DashboardPage.xaml (lines 95-201).
 *
 * Xamarin layout:
 *  - Frame: bg=CompletedTaskInsightsBackground (#7ED1DC), CornerRadius=8
 *  - Grid: 5 columns (*,Auto,*,Auto,*)
 *  - Each column: Horizontal StackLayout with icon (20px) + label
 *  - Icons: TotalTask (total_task.svg), MoveBlue (move_blue.svg), AuditBlue (audit_blue.svg)
 *  - Labels: FontSize=14 (phone), PoppinsRegular title + PoppinsBold count, BlackLabelColor
 *  - Dividers: BoxView w=1, bg=white
 */
export function CompletedInsightsBar({ tasks }: CompletedInsightsBarProps) {
  const { t } = useTranslation();
  const total = tasks.length;
  const moveCount = tasks.filter((t) => t.TaskType === 'Move').length;
  const auditCount = tasks.filter((t) => t.TaskType === 'Audit').length;

  return (
    <div className="mx-[10px] my-[8px] rounded-lg bg-[#7ED1DC] sm:px-[5px] py-[10px]">
      <div className="flex items-center">
        {/* Total task — Xamarin: SvgCachedImage TotalTask 20px + Label */}
        <div className="flex flex-1 min-w-0 items-center justify-center gap-1.5 overflow-hidden">
          <div className="flex w-full flex-col items-center min-w-0 md:flex-row md:justify-center md:gap-1.5">
            <div className="flex max-w-full items-center gap-1 min-w-0">
              <img
                src={IMAGE_CONSTANTS.totalTask}
                alt=""
                className="h-5 w-5 shrink-0 object-contain"
              />
              <span
                className="text-xs md:text-sm font-poppins text-black truncate"
                title={t('dashboard.totalTask')}
              >
                {t('dashboard.totalTask')}
              </span>
            </div>
            <span className="text-sm font-poppins font-bold text-black shrink-0">{total}</span>
          </div>
        </div>

        {/* Divider */}
        <div className="w-px self-stretch bg-white" />

        {/* Move task — Xamarin: SvgCachedImage MoveBlue 20px + Label */}
        <div className="flex flex-1 min-w-0 items-center justify-center gap-1.5 overflow-hidden">
          <div className="flex w-full flex-col items-center min-w-0 md:flex-row md:justify-center md:gap-1.5">
            <div className="flex max-w-full items-center gap-1 min-w-0">
              <img
                src={IMAGE_CONSTANTS.moveBlue}
                alt=""
                className="h-5 w-5 shrink-0 object-contain"
              />
              <span
                className="text-xs md:text-sm font-poppins text-black truncate"
                title={t('dashboard.moveTaskLabel')}
              >
                {t('dashboard.moveTaskLabel')}
              </span>
            </div>
            <span className="text-sm font-poppins font-bold text-black shrink-0">{moveCount}</span>
          </div>
        </div>

        {/* Divider */}
        <div className="w-px self-stretch bg-white" />

        {/* Audit task — Xamarin: SvgCachedImage AuditBlue 20px + Label */}
        <div className="flex flex-1 min-w-0 items-center justify-center gap-1.5 overflow-hidden">
          <div className="flex w-full flex-col items-center min-w-0 md:flex-row md:justify-center md:gap-1.5">
            <div className="flex max-w-full items-center gap-1 min-w-0">
              <img
                src={IMAGE_CONSTANTS.auditBlue}
                alt=""
                className="h-5 w-5 shrink-0 object-contain"
              />
              <span
                className="text-xs md:text-sm font-poppins text-black truncate"
                title={t('dashboard.auditTaskLabel')}
              >
                {t('dashboard.auditTaskLabel')}
              </span>
            </div>
            <span className="text-sm font-poppins font-bold text-black shrink-0">{auditCount}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
